import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  TextField
} from '@mui/material';
import { FlowBreadcrumbs, FlowFormCard, SubmitButton, mediaBaseUrl, mediaPreviewStyles } from './FlowLayout';

const AddEditTourType = () => {
  const { id, continentId } = useParams();
  const navigate = useNavigate();
  const [continentName, setContinentName] = useState('');
  const [form, setForm] = useState({
    tour_type: '',
    heading: '',
    desc_txt: '',
    banner_type: 'image',
    banner_src: null,
    banner_alt: '',
    banner_title: '',
    banner_description: '',
    current_banner_src: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    axios.get(`https://aaryanholidays.in/backend/continents/${continentId}`)
      .then((response) => setContinentName(response.data.continent))
      .catch((error) => console.error('Error fetching continent name:', error));

    if (id) {
      axios.get(`https://aaryanholidays.in/backend/tour-types/${id}`)
        .then((response) => {
          const data = response.data;
          setForm({
            tour_type: data.tour_type,
            heading: data.heading,
            desc_txt: data.desc_txt,
            banner_type: data.banner_type,
            banner_src: null,
            banner_alt: data.banner_alt,
            banner_title: data.banner_title,
            banner_description: data.banner_description,
            current_banner_src: data.banner_src
          });
        })
        .catch((error) => console.error('Error fetching the tour type:', error));
    }
  }, [id, continentId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    const formData = new FormData();
    formData.append('tour_type', form.tour_type);
    formData.append('heading', form.heading);
    formData.append('desc_txt', form.desc_txt);
    formData.append('banner_type', form.banner_type);
    if (form.banner_src) {
      formData.append('banner_src', form.banner_src);
    }
    formData.append('banner_alt', form.banner_alt);
    formData.append('banner_title', form.banner_title);
    formData.append('banner_description', form.banner_description);
    formData.append('continent', continentId);

    try {
      if (id) {
        await axios.put(`https://aaryanholidays.in/backend/tour-types/${id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axios.post('https://aaryanholidays.in/backend/tour-types', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      setSnackbar({ open: true, message: `Country ${id ? 'updated' : 'created'} successfully.`, severity: 'success' });
      navigate(`/manage-tour-type/${continentId}`);
    } catch (error) {
      console.error('There was an error saving the tour type!', error);
      setSnackbar({ open: true, message: 'There was an error saving the country.', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <FlowBreadcrumbs
        items={[
          { label: 'Tour Management', to: '/manage-continents' },
          { label: continentName || 'Continent', to: `/manage-tour-type/${continentId}` },
          { label: id ? 'Edit Country' : 'Add Country' }
        ]}
      />
      <FlowFormCard
        title={id ? 'Edit Country' : 'Add Country'}
        subtitle="Create the country page entry with polished banner media, stronger copy, and a clear place in the hierarchy."
      >
        <form onSubmit={handleSubmit}>
          <Stack spacing={2.5}>
            <TextField label="Country Name" value={form.tour_type} onChange={(e) => setForm({ ...form, tour_type: e.target.value })} fullWidth required />
            <TextField label="Heading" value={form.heading} onChange={(e) => setForm({ ...form, heading: e.target.value })} fullWidth required />
            <TextField label="Description" value={form.desc_txt} onChange={(e) => setForm({ ...form, desc_txt: e.target.value })} fullWidth multiline rows={5} required />
            <FormControl>
              <FormLabel>Banner Type</FormLabel>
              <RadioGroup row value={form.banner_type} onChange={(e) => setForm({ ...form, banner_type: e.target.value })}>
                <FormControlLabel value="image" control={<Radio />} label="Image" />
                <FormControlLabel value="video" control={<Radio />} label="Video" />
              </RadioGroup>
            </FormControl>
            <Box>
              <input id="tour-banner-upload" type="file" hidden accept={form.banner_type === 'image' ? 'image/*' : 'video/*'} onChange={(e) => setForm({ ...form, banner_src: e.target.files[0] })} />
              <label htmlFor="tour-banner-upload">
                <SubmitButton component="span" variant="outlined">
                  Upload Banner
                </SubmitButton>
              </label>
            </Box>
            {(form.banner_src || form.current_banner_src) && form.banner_type === 'image' ? (
              <img
                src={form.banner_src ? URL.createObjectURL(form.banner_src) : `${mediaBaseUrl}${form.current_banner_src}`}
                alt="Country banner preview"
                style={mediaPreviewStyles}
              />
            ) : null}
            {(form.banner_src || form.current_banner_src) && form.banner_type === 'video' ? (
              <video
                src={form.banner_src ? URL.createObjectURL(form.banner_src) : `${mediaBaseUrl}${form.current_banner_src}`}
                controls
                style={mediaPreviewStyles}
              />
            ) : null}
            <TextField label="Banner Alt" value={form.banner_alt} onChange={(e) => setForm({ ...form, banner_alt: e.target.value })} fullWidth />
            <TextField label="Banner Title" value={form.banner_title} onChange={(e) => setForm({ ...form, banner_title: e.target.value })} fullWidth />
            <TextField label="Banner Description" value={form.banner_description} onChange={(e) => setForm({ ...form, banner_description: e.target.value })} fullWidth multiline rows={4} />
            <SubmitButton type="submit" variant="contained" loading={submitting}>
              {id ? 'Update Country' : 'Create Country'}
            </SubmitButton>
          </Stack>
        </form>
      </FlowFormCard>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddEditTourType;
